import React from "react";
import clsx from "clsx";
import { AppColor, ButtonColor, ButtonSize } from "../../types/styling";
import { AppButtonColors, ThemeColors } from "../../constants/theme";
import { getValueOrDefault } from "@og-eternity/client-lib";
import Title from "./Title";
import Spinner from "./Spinner";

export type ButtonProps = {
  size?: ButtonSize
  color?: AppColor | ButtonColor
  loading?: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((
  {
    size = "md",
    color = "primary",
    loading,
    disabled,
    children,
    className,
    ["aria-label"]: ariaLabel,
    ...rest
  }: ButtonProps,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  const btnColor = getValueOrDefault(AppButtonColors, color, color)

  return (
    <button
      aria-label={ariaLabel}
      title={ariaLabel}
      ref={ref}
      type="button"
      disabled={disabled || loading}
      className={clsx(
        className,
        "select-none",
        size === "circle" && 'w-btn-circle h-btn-circle bg-btn-circle-blue hover:bg-btn-circle-blue-hover',

        size === "square-sm" && btnColor === "blue" && `w-btn-square-sm h-btn-sm bg-btn-square-sm-blue hover:bg-btn-square-sm-blue-hover`,
        size === "square" && btnColor === "blue" && `w-btn-square h-btn bg-btn-square-blue hover:bg-btn-square-blue-hover`,
        size === "sm" && btnColor === "blue" && "w-btn-md h-btn-sm bg-btn-sm-blue hover:bg-btn-sm-blue-hover",
        size === "md" && btnColor === "blue" && "w-btn-md h-btn bg-btn-md-blue hover:bg-btn-md-blue-hover",
        size === "lg" && btnColor === "blue" && "w-btn-lg h-btn bg-btn-lg-blue hover:bg-btn-lg-blue-hover",
        size === "xl" && btnColor === "blue" && "w-btn-xl h-btn bg-btn-xl-blue hover:bg-btn-xl-blue-hover",

        size === "square-sm" && btnColor === "blue-dark" && `w-btn-square-sm h-btn-sm bg-btn-square-sm-blue-dark hover:bg-btn-square-sm-blue-dark-hover`,
        size === "square" && btnColor === "blue-dark" && "w-btn-square h-btn bg-btn-square-blue-dark hover:bg-btn-square-blue-dark-hover",
        size === "sm" && btnColor === "blue-dark" && "w-btn-md h-btn-sm bg-btn-sm-blue-dark hover:bg-btn-sm-blue-dark-hover",
        size === "md" && btnColor === "blue-dark" && "w-btn-md h-btn bg-btn-md-blue-dark hover:bg-btn-md-blue-dark-hover",
        size === "lg" && btnColor === "blue-dark" && "w-btn-lg h-btn bg-btn-lg-blue-dark hover:bg-btn-lg-blue-dark-hover",
        size === "xl" && btnColor === "blue-dark" && "w-btn-xl h-btn bg-btn-xl-blue-dark hover:bg-btn-xl-blue-dark-hover",

        size === "square-sm" && btnColor === "green" && `w-btn-square-sm h-btn-sm bg-btn-square-sm-green hover:bg-btn-square-sm-green-hover`,
        size === "square" && btnColor === "green" && "w-btn-square h-btn bg-btn-square-green hover:bg-btn-square-green-hover",
        size === "sm" && btnColor === "green" && "w-btn-md h-btn-sm bg-btn-sm-green hover:bg-btn-sm-green-hover",
        size === "md" && btnColor === "green" && "w-btn-md h-btn bg-btn-md-green hover:bg-btn-md-green-hover",
        size === "lg" && btnColor === "green" && "w-btn-lg h-btn bg-btn-lg-green hover:bg-btn-lg-green-hover",
        size === "xl" && btnColor === "green" && "w-btn-xl h-btn bg-btn-xl-green hover:bg-btn-xl-green-hover",

        size === "square-sm" && btnColor === "red" && `w-btn-square-sm h-btn-sm bg-btn-square-sm-red hover:bg-btn-square-sm-red-hover`,
        size === "square" && btnColor === "red" && "w-btn-square h-btn bg-btn-square-red hover:bg-btn-square-red-hover",
        size === "sm" && btnColor === "red" && "w-btn-md h-btn-sm bg-btn-sm-red hover:bg-btn-sm-red-hover",
        size === "md" && btnColor === "red" && "w-btn-md h-btn bg-btn-md-red hover:bg-btn-md-red-hover",
        size === "lg" && btnColor === "red" && "w-btn-lg h-btn bg-btn-lg-red hover:bg-btn-lg-red-hover",
        size === "xl" && btnColor === "red" && "w-btn-xl h-btn bg-btn-xl-red hover:bg-btn-xl-red-hover",

        size === "square-sm" && btnColor === "brown" && `w-btn-square-sm h-btn-sm bg-btn-square-sm-brown hover:bg-btn-square-sm-brown-hover`,
        size === "square" && btnColor === "brown" && "w-btn-square h-btn bg-btn-square-brown hover:bg-btn-square-brown-hover",
        size === "sm" && btnColor === "brown" && "w-btn-md h-btn-sm bg-btn-sm-brown hover:bg-btn-sm-brown-hover",
        size === "md" && btnColor === "brown" && "w-btn-md h-btn bg-btn-md-brown hover:bg-btn-md-brown-hover",
        size === "lg" && btnColor === "brown" && "w-btn-lg h-btn bg-btn-lg-brown hover:bg-btn-lg-brown-hover",
        size === "xl" && btnColor === "brown" && "w-btn-xl h-btn bg-btn-xl-brown hover:bg-btn-xl-brown-hover",

        loading ? "text-opacity-75" : "disabled:grayscale",
        "flex flex-row justify-center items-center",
        "text-white font-text font-bold",
        "disabled:cursor-game-cant",
      )}
      {...rest}
    >
      {loading && (
        <Spinner className={clsx("h-4 w-4", size !== "square" && "mr-2")} color={ThemeColors.gold.DEFAULT} />
      )}
      {(size !== "square" || !loading) && (
        typeof children === "string" ? (
          <Title className="text-title-sm-md">{children}</Title>
        ) : children
      )}
    </button>
  )
})

export type ButtonCircleCloseProps = {
} & React.ButtonHTMLAttributes<HTMLButtonElement>

export function ButtonCircleClose({
  className,
  ...rest
}: ButtonCircleCloseProps) {

  return (
    <button
      className={clsx(
        className,
        "w-10 h-10 bg-btn-circle-close hover:bg-btn-circle-close-hover",
        "disabled:opacity-75 disabled:cursor-game-cant",
      )}
      {...rest}
    />
  )
}

export function ButtonDiamondClose({
  className,
  ...rest
}: ButtonCircleCloseProps) {

  return (
    <button
      className={clsx(
        className,
        "w-9 h-9 bg-btn-diamond-close",
        "disabled:opacity-75 disabled:cursor-game-cant",
      )}
      {...rest}
    />
  )
}

export function ButtonDiamondBack({
  className,
  ...rest
}: ButtonCircleCloseProps) {

  return (
    <button
      className={clsx(
        className,
        "w-9 h-9 bg-btn-diamond-back",
        "disabled:opacity-75 disabled:cursor-game-cant",
      )}
      {...rest}
    />
  )
}

export function ButtonDiamondCheck({
  className,
  ...rest
}: ButtonCircleCloseProps) {

  return (
    <button
      className={clsx(
        className,
        "w-9 h-9 bg-btn-diamond-check",
        "disabled:opacity-75 disabled:cursor-game-cant",
      )}
      {...rest}
    />
  )
}

export function ButtonDiamondSetting({
  className,
  ...rest
}: ButtonCircleCloseProps) {

  return (
    <button
      className={clsx(
        className,
        "w-9 h-9 bg-btn-diamond-setting",
        "disabled:opacity-75 disabled:cursor-game-cant",
      )}
      {...rest}
    />
  )
}



export default Button;